/* eslint-disable react-hooks/exhaustive-deps */
import TimeCell from "../../components/TimeCell";
import { useState, useEffect, useMemo } from "react";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import DataGridButton from "../../components/DataGridButton";
import { Box, Grid, Typography, useTheme } from "@mui/material";
import { Sync } from "@mui/icons-material";
import DataTable from "../../components/DataTable";
import PrFilterBar from "./PrFilterBar";
import { useGetDCPrs } from "../../api/pullRequests";
import RedirectLink from "../../components/RedirectLink";
import { dataGridButtonTypography } from "../../styles/DataGridStyles";
import { dataGridButtonStyles } from "../../styles/buttonStyles";
import { tabbedDataGridPage } from "../../styles/commonPageStyles";
import utilColors from "../../styles/utilColors";
import styles from "./styles";

const PendingDoubleCommitMerges = () => {
  const theme = useTheme();
  const colors = tokens(theme.paletteId);

  const [prs, setPrs] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedFilters, setSelectedFilters] = useState({});

  const {
    postData: getPrs,
    response: getPrsResp,
    isLoading: isLoadingPrs,
  } = useGetDCPrs();

  useEffect(() => {
    getPrs();
  }, []);

  useEffect(() => {
    if (Array.isArray(getPrsResp?.response)) {
      setPrs(getPrsResp.response);
    }
  }, [getPrsResp]);

  // Filter options derived from the loaded rows.
  const filters = useMemo(() => {
    const distinct = (field) =>
      [...new Set(prs.map((pr) => pr?.[field]).filter(Boolean))].sort();
    return [
      {
        name: "repository",
        label: "Repository",
        options: distinct("repository"),
      },
      { name: "base_branch", label: "Base", options: distinct("base_branch") },
      { name: "author", label: "Author", options: distinct("author") },
    ];
  }, [prs]);

  const filteredPrs = useMemo(() => {
    const query = search.trim().toLowerCase();
    return prs.filter(
      (pr) =>
        (!query || pr?.title?.toLowerCase().includes(query)) &&
        Object.entries(selectedFilters).every(
          ([field, values]) => !values?.length || values.includes(pr?.[field])
        )
    );
  }, [prs, search, selectedFilters]);

  const columns = [
    {
      field: "repository",
      renderHeader: () => "Repository Name",
      flex: 2.5,
      cellClassName: "name-column--cell",
      renderCell: ({ row: { repository } }) => repository || "",
    },
    {
      field: "title",
      flex: 4.5,
      renderHeader: () => "PR Title",
      renderCell: ({ row: { title, html_url } }) => (
        <RedirectLink href={html_url} color={utilColors(colors).skyBlue}>
          <Box component="span" sx={styles.linkText}>
            {title ?? ""}
          </Box>
        </RedirectLink>
      ),
    },
    {
      field: "base_branch",
      flex: 1,
      renderHeader: () => "Base",
      renderCell: ({ row: { base_branch } }) => base_branch ?? "",
    },
    {
      field: "author",
      flex: 1.5,
      renderHeader: () => "Author",
      renderCell: ({ row: { author } }) => author ?? "",
    },
    {
      field: "created_at",
      flex: 1.3,
      minWidth: 118,
      renderHeader: () => "Created At",
      renderCell: ({ row: { created_at } }) => <TimeCell value={created_at} />,
    },
  ];

  return (
    <Box sx={styles.mainBox}>
      <Grid sx={styles.headerGrid} container columns={2}>
        <Grid xs={1}>
          <Header
            title="Pending Double Commit Merges"
            subtitle="See the list of double commits pending to be merged"
          />
        </Grid>
        <Grid xs={1}>
          <Box sx={tabbedDataGridPage.topButtonStack}>
            <DataGridButton
              sx={dataGridButtonStyles.pageTopButtons}
              onClick={() => getPrs()}
            >
              <Sync sx={{ fontSize: 16, color: "#fff" }} />
              <Typography sx={dataGridButtonTypography(colors, 500)}>
                RELOAD
              </Typography>
            </DataGridButton>
          </Box>
        </Grid>
      </Grid>
      <PrFilterBar
        filters={filters}
        selected={selectedFilters}
        onChange={(name, values) =>
          setSelectedFilters((prev) => ({ ...prev, [name]: values }))
        }
        onClearAll={() => setSelectedFilters({})}
        search={search}
        onSearchChange={setSearch}
        filteredCount={filteredPrs.length}
        totalCount={prs.length}
        itemsNoun="pending PRs"
      />
      <DataTable
        height="100%"
        sx={styles.dataTable}
        getRowId={(row) => row?.api_url}
        loading={isLoadingPrs}
        rows={filteredPrs}
        columns={columns}
      />
    </Box>
  );
};

export default PendingDoubleCommitMerges;
